import React, { useEffect, useRef, useState } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import "./Header.css";

const SPECS = [
  { label: "Role", value: "Software Development Engineer" },
  { label: "Desk", value: "Capital Group" },
  { label: "Stack", value: "C# / TypeScript / Python / Terraform / AWS" },
  { label: "Based", value: "Irvine, CA · 33.67°N" },
  { label: "Current", value: "Frontend · backend services · data pipelines · cloud infrastructure" },
  { label: "Exploring", value: "Agentic software development · Claude APIs · the beach · matcha · Game of Thrones" },
];

const TENORS = [
  { x: 0, y: 258, label: "1Y" },
  { x: 280, y: 224, label: "2Y" },
  { x: 520, y: 152, label: "5Y" },
  { x: 760, y: 88, label: "10Y" },
  { x: 1000, y: 52, label: "30Y" },
];

const useLiveTimePST = () => {
  const [time, setTime] = useState("");
  useEffect(() => {
    const fmt = () =>
      setTime(
        new Date().toLocaleTimeString("en-US", {
          hour12: false,
          timeZone: "America/Los_Angeles",
        })
      );
    fmt();
    const id = setInterval(fmt, 1000);
    return () => clearInterval(id);
  }, []);
  return time;
};

const useBondMarketStatus = () => {
  // US Treasury / SIFMA recommended bond hours: 8:00–17:00 ET, Mon–Fri.
  const [open, setOpen] = useState(false);
  useEffect(() => {
    const compute = () => {
      const nowET = new Date(
        new Date().toLocaleString("en-US", { timeZone: "America/New_York" })
      );
      const day = nowET.getDay(); // 0 Sun, 6 Sat
      const h = nowET.getHours();
      const m = nowET.getMinutes();
      const decimal = h + m / 60;
      setOpen(day >= 1 && day <= 5 && decimal >= 8 && decimal < 17);
    };
    compute();
    const id = setInterval(compute, 30000);
    return () => clearInterval(id);
  }, []);
  return open;
};

const Header = () => {
  const ref = useRef(null);
  const time = useLiveTimePST();
  const marketOpen = useBondMarketStatus();

  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });

  const heroY = useTransform(scrollYProgress, [0, 1], [0, -120]);
  const heroOpacity = useTransform(scrollYProgress, [0, 0.6, 1], [1, 0.5, 0]);
  const cueOpacity = useTransform(scrollYProgress, [0, 0.2], [1, 0]);

  return (
    <header id="home" ref={ref} className="hero">
      <div className="hero-pinstripes" aria-hidden="true" />
      <div className="hero-grain" aria-hidden="true" />

      {/* Self-drawing yield curve — the signature visual */}
      <svg
        className="hero-curve"
        viewBox="0 0 1000 300"
        preserveAspectRatio="none"
        aria-hidden="true"
      >
        <defs>
          <linearGradient id="hero-curve-grad" x1="0" y1="0" x2="1" y2="0">
            <stop offset="0%" stopColor="rgba(212, 163, 115, 0)" />
            <stop offset="22%" stopColor="rgba(212, 163, 115, 0.6)" />
            <stop offset="100%" stopColor="rgba(212, 163, 115, 0.32)" />
          </linearGradient>
        </defs>

        {/* Tenor grid lines */}
        {TENORS.map((t, i) => (
          <line
            key={`g-${t.label}`}
            x1={t.x}
            y1="0"
            x2={t.x}
            y2="300"
            stroke="rgba(212, 163, 115, 0.08)"
            strokeDasharray="2 6"
            className="hero-curve-grid"
            style={{ animationDelay: `${1.5 + i * 0.08}s` }}
          />
        ))}

        {/* The curve itself */}
        <path
          d="M 0 258 Q 160 252 280 224 T 520 152 T 760 88 T 1000 52"
          fill="none"
          stroke="url(#hero-curve-grad)"
          strokeWidth="1.4"
          strokeLinecap="round"
          className="hero-curve-path"
        />

        {/* Tenor markers (dots on curve) */}
        {TENORS.map((t, i) => (
          <circle
            key={`m-${t.label}`}
            cx={t.x}
            cy={t.y}
            r="3"
            fill="#0d0c0a"
            stroke="rgba(212, 163, 115, 0.85)"
            strokeWidth="1.2"
            className="hero-curve-mark"
            style={{ animationDelay: `${2.5 + i * 0.08}s` }}
          />
        ))}
      </svg>

      {/* Top status bar */}
      <div className="hero-statusbar">
        <span className="hero-eyebrow">
          <span className="hero-eyebrow-dot" /> Currently — Engineering at
          Capital Group
        </span>
        <div className="hero-clock-group">
          <span
            className={`hero-market-pill ${
              marketOpen ? "hero-market-pill--open" : ""
            }`}
          >
            <span className="hero-market-bullet" />
            US Bond Mkt · {marketOpen ? "Open" : "Closed"}
          </span>
          <span className="hero-clock">
            <span className="hero-clock-label">LAX</span>
            <span className="hero-clock-digits">{time || "00:00:00"}</span>
          </span>
        </div>
      </div>

      <motion.div
        className="hero-grid"
        style={{ y: heroY, opacity: heroOpacity }}
      >
        {/* Left — name */}
        <div className="hero-name-block">
          <div className="hero-name-meta">
            <span className="hero-name-meta-num">ED.001</span>
            <span className="hero-name-meta-line" aria-hidden="true" />
            <span className="hero-name-meta-label">Index of self</span>
          </div>

          <h1 className="hero-name">
            <span className="hero-name-first">Eduardo</span>
            <span className="hero-name-last">
              <em>Nunez</em>
              <span className="hero-name-period">.</span>
            </span>
          </h1>

          <p className="hero-role">
            Software engineer building front-office trading systems on
            event-driven architectures.
          </p>
        </div>

        {/* Right — spec sheet */}
        <aside className="hero-spec">
          <header className="hero-spec-head">
            <span className="hero-spec-title">Specifications</span>
            <span className="hero-spec-id">ED.001 · 2024</span>
          </header>

          <ul className="hero-spec-list">
            {SPECS.map((row, i) => (
              <li
                key={row.label}
                className="hero-spec-row"
                style={{ animationDelay: `${0.55 + i * 0.09}s` }}
              >
                <span className="hero-spec-num">
                  {String(i + 1).padStart(2, "0")}
                </span>
                <span className="hero-spec-label">{row.label}</span>
                <span className="hero-spec-value">{row.value}</span>
              </li>
            ))}
          </ul>

          <footer className="hero-spec-foot">
            <a
              href="https://github.com/eddayyy"
              target="_blank"
              rel="noopener noreferrer"
              className="hero-spec-link"
            >
              GitHub <span className="hero-spec-link-arrow">↗</span>
            </a>
            <a
              href="https://www.linkedin.com/in/eduardong"
              target="_blank"
              rel="noopener noreferrer"
              className="hero-spec-link"
            >
              LinkedIn <span className="hero-spec-link-arrow">↗</span>
            </a>
            <a
              href="mailto:eduardonunez.eng@gmail.com"
              className="hero-spec-link"
            >
              Email <span className="hero-spec-link-arrow">↗</span>
            </a>
          </footer>
        </aside>
      </motion.div>

      <motion.a
        href="#education"
        className="hero-cue"
        style={{ opacity: cueOpacity }}
        aria-label="Scroll to next section"
      >
        <span className="hero-cue-label">Scroll</span>
        <motion.span
          className="hero-cue-line"
          aria-hidden="true"
          animate={{ scaleY: [0, 1, 0] }}
          transition={{
            duration: 2.2,
            repeat: Infinity,
            ease: "easeInOut",
            times: [0, 0.5, 1],
          }}
        />
      </motion.a>
    </header>
  );
};

export default Header;
